import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Redirect } from "react-router-dom";
import {
  FaBusinessTime,
  FaCalendarAlt,
  FaCheck,
  FaEdit,
  FaBan,
  FaSyncAlt,
  FaTimes,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import BackButton from "../ChildComponent/BackButton";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import MdlSelectSession from "../dialog/MdlSelectSession";
import COLORS from "../resources/colors/COLORS";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

//Created By Cursor
function logFullUrl(endpoint, params = {}) {
  const qs = new URLSearchParams(params).toString();
  const url = `${API_BASE()}${endpoint}${qs ? `?${qs}` : ""}`;
  console.log("Full URL: ", url);
  return url;
}

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  '"Segoe UI", system-ui, -apple-system, Roboto, "Helvetica Neue", Arial, sans-serif';

function isManualRow(item) {
  return (
    item?.is_manual === 1 ||
    item?.is_manual === "1" ||
    item?.is_manual === true
  );
}

function isActiveRow(item) {
  const s = String(item?.status || "Active").trim();
  return s.toLowerCase() !== "inactive";
}

function toInt(value, fallback = 0) {
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : fallback;
}

//Created By Cursor
const WorkingDayRow = memo(function WorkingDayRow({
  item,
  index,
  busy,
  onToggleStatus,
}) {
  const manual = isManualRow(item);
  const active = isActiveRow(item);
  return (
    <tr
      className={`swd-row${manual ? " swd-row--manual" : ""}${
        !active ? " swd-row--inactive" : ""
      }`}
      style={{ "--swd-i": index }}
    >
      <td className="swd-td-sr">
        <span className="swd-sr">{index + 1}</span>
      </td>
      <td className="swd-td-code">{item.month || "—"}</td>
      <td>
        <div className="swd-month-cell">
          <strong>{item.month2 || "—"}</strong>
        </div>
      </td>
      <td>{item.days_in_month ?? "—"}</td>
      <td>{item.week_off ?? "—"}</td>
      <td>{item.holidays ?? "—"}</td>
      <td>
        <span className="swd-working-pill">{item.working_days ?? "—"}</span>
      </td>
      <td>
        <span
          className={`swd-mode-pill ${
            manual ? "swd-mode-pill--manual" : "swd-mode-pill--auto"
          }`}
        >
          {manual ? "Manual" : "Auto"}
        </span>
      </td>
      <td>
        <div className="swd-status-cell">
          <span
            className={`swd-status-pill ${
              active ? "swd-status-pill--active" : "swd-status-pill--inactive"
            }`}
          >
            <span className="swd-status-pill__dot" aria-hidden />
            {active ? "Active" : "InActive"}
          </span>
          <button
            type="button"
            className={`swd-status-toggle ${
              active ? "swd-status-toggle--off" : "swd-status-toggle--on"
            }`}
            title={active ? "Mark InActive" : "Mark Active"}
            disabled={busy}
            onClick={() => onToggleStatus(item)}
          >
            {active ? (
              <>
                <FaBan aria-hidden /> InActive
              </>
            ) : (
              <>
                <FaCheck aria-hidden /> Active
              </>
            )}
          </button>
        </div>
      </td>
    </tr>
  );
});

//Created By Cursor
function MdlEditAllMonths({
  open,
  saving,
  formRows,
  liveStats,
  sessionName,
  onChange,
  onClose,
  onSave,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !saving && onClose) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, saving, onClose]);

  if (!open) return null;

  const modal = (
    <div
      className="swd-mdl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="swd-mdl-all-title"
    >
      <button
        type="button"
        className="swd-mdl__backdrop"
        aria-label="Close"
        onClick={onClose}
        disabled={saving}
      />
      <div className="swd-mdl__panel swd-mdl__panel--wide">
        <header className="swd-mdl__hero">
          <div className="swd-mdl__hero-icon" aria-hidden>
            <FaEdit />
          </div>
          <div className="min-w-0">
            <h2 id="swd-mdl-all-title" className="swd-mdl__title">
              Edit working days
            </h2>
            <p className="swd-mdl__sub">
              {sessionName || "Session"} · {liveStats.months} active month
              {liveStats.months === 1 ? "" : "s"} · live totals update as you type
            </p>
          </div>
          <button
            type="button"
            className="swd-mdl__close"
            onClick={onClose}
            disabled={saving}
            aria-label="Close dialog"
          >
            <FaTimes />
          </button>
        </header>

        <div className="swd-mdl__scroll">
          <div className="swd-mdl__stats" aria-label="Session totals from form">
            <div className="swd-mdl__stat">
              <span>Months</span>
              <strong>{liveStats.months}</strong>
            </div>
            <div className="swd-mdl__stat">
              <span>Total days</span>
              <strong>{liveStats.days}</strong>
            </div>
            <div className="swd-mdl__stat">
              <span>Week offs</span>
              <strong>{liveStats.weekOff}</strong>
            </div>
            <div className="swd-mdl__stat">
              <span>Holidays</span>
              <strong>{liveStats.holidays}</strong>
            </div>
            <div className="swd-mdl__stat swd-mdl__stat--accent">
              <span>Working days</span>
              <strong>{liveStats.working}</strong>
            </div>
          </div>

          <p className="swd-mdl__note">
            All fields are <strong>required</strong>. Changing days, week off, or
            holidays auto-updates working days unless you edit working days
            manually for that month.
          </p>

          <div className="swd-edit-table-wrap">
            <table className="swd-edit-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Days *</th>
                  <th>Week off *</th>
                  <th>Holidays *</th>
                  <th>Working *</th>
                </tr>
              </thead>
              <tbody>
                {formRows.map((row) => (
                  <tr key={row.month}>
                    <td className="swd-edit-table__month">
                      <strong>{row.month2 || row.month}</strong>
                      <small>{row.month}</small>
                    </td>
                    <td>
                      <input
                        type="number"
                        min={1}
                        max={31}
                        value={row.days_in_month}
                        onChange={(e) =>
                          onChange(row.month, "days_in_month", e.target.value)
                        }
                        disabled={saving}
                        required
                        aria-label={`${row.month2 || row.month} days in month`}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min={0}
                        max={31}
                        value={row.week_off}
                        onChange={(e) =>
                          onChange(row.month, "week_off", e.target.value)
                        }
                        disabled={saving}
                        required
                        aria-label={`${row.month2 || row.month} week off`}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min={0}
                        max={31}
                        value={row.holidays}
                        onChange={(e) =>
                          onChange(row.month, "holidays", e.target.value)
                        }
                        disabled={saving}
                        required
                        aria-label={`${row.month2 || row.month} holidays`}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min={0}
                        max={31}
                        value={row.working_days}
                        onChange={(e) =>
                          onChange(
                            row.month,
                            "working_days",
                            e.target.value,
                            true
                          )
                        }
                        disabled={saving}
                        required
                        aria-label={`${row.month2 || row.month} working days`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="swd-mdl__footer">
          <button
            type="button"
            className="swd-btn swd-btn--ghost"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="swd-btn swd-btn--primary"
            onClick={onSave}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

//Created By Cursor
export default function SchoolWorkingDays(props) {
  const [loggedIn] = useState(() => !!localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removingMonth, setRemovingMonth] = useState("");
  const [rows, setRows] = useState([]);
  const [allEditOpen, setAllEditOpen] = useState(false);
  const [editRows, setEditRows] = useState([]);
  const [workingTouchedMonths, setWorkingTouchedMonths] = useState(() => new Set());
  const [sessionId, setSessionId] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);

  // Resolve initial session: URL param (report-card flow) → current session
  useEffect(() => {
    const fromParams = props.match?.params?.session_id;
    if (fromParams && fromParams !== "null" && fromParams !== "undefined") {
      setSessionId(fromParams);
      try {
        const list = SessionManager.getCurrentSession?.() || [];
        const match = list.find((s) => s.session_id === fromParams);
        setSessionName(match?.session_name || fromParams);
      } catch (e) {
        setSessionName(fromParams);
      }
      setSessionReady(true);
      return;
    }
    try {
      const current = SessionManager.getCurrentSession()?.[0];
      if (current?.session_id) {
        setSessionId(current.session_id);
        setSessionName(current.session_name || "");
      }
    } catch (e) {
      // ignore
    }
    setSessionReady(true);
  }, [props.match?.params?.session_id]);

  const hasManualEntry = useMemo(
    () => rows.some((r) => isActiveRow(r) && isManualRow(r)),
    [rows]
  );

  const manualCount = useMemo(
    () => rows.filter((r) => isActiveRow(r) && isManualRow(r)).length,
    [rows]
  );

  const stats = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        if (!isActiveRow(r)) return acc;
        acc.months += 1;
        acc.days += toInt(r.days_in_month);
        acc.weekOff += toInt(r.week_off);
        acc.holidays += toInt(r.holidays);
        acc.working += toInt(r.working_days);
        return acc;
      },
      { months: 0, days: 0, weekOff: 0, holidays: 0, working: 0 }
    );
  }, [rows]);

  const liveEditStats = useMemo(() => {
    return editRows.reduce(
      (acc, r) => {
        acc.months += 1;
        acc.days += toInt(r.days_in_month);
        acc.weekOff += toInt(r.week_off);
        acc.holidays += toInt(r.holidays);
        acc.working += toInt(r.working_days);
        return acc;
      },
      { months: 0, days: 0, weekOff: 0, holidays: 0, working: 0 }
    );
  }, [editRows]);

  const loadRows = useCallback(
    async ({ soft = false } = {}) => {
      if (!sessionId) {
        setRows([]);
        setLoading(false);
        return;
      }
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) {
        setRows([]);
        setLoading(false);
        myToast.notify_warning("Missing school code.");
        return;
      }

      if (soft) setRefreshing(true);
      else setLoading(true);

      try {
        const params = { school_code: schoolCode, session_id: sessionId };
        logFullUrl("getSchoolWorkingDays.php", params);
        const response = await axios
          .get(`${API_BASE()}getSchoolWorkingDays.php`, { params })
          .then((res) => res.data);

        if (response?.api_status === "OK") {
          const data = Array.isArray(response.data) ? response.data : [];
          // Keep only this session's rows (defensive)
          setRows(
            data.filter(
              (r) => !r.session_id || String(r.session_id) === String(sessionId)
            )
          );
        } else {
          setRows([]);
          myToast.notify_warning(
            response?.api_remark || "Failed to load working days."
          );
        }
      } catch (error) {
        console.error("Error fetching school working days:", error);
        setRows([]);
        myToast.notify_warning("Failed to load working days.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [sessionId]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!loggedIn || !sessionReady) return;
    if (!sessionId) {
      setLoading(false);
      setRows([]);
      return;
    }
    loadRows();
  }, [loggedIn, sessionReady, sessionId, loadRows]);

  const onSessionPicked = useCallback((id, name) => {
    setSessionId(id);
    setSessionName(name || "");
    setSessionDialogOpen(false);
    setRows([]);
  }, []);

  //Created By Cursor
  const openAllEdit = useCallback(() => {
    const activeRows = rows.filter((r) => isActiveRow(r));
    if (!activeRows.length) {
      myToast.notify_warning("No active months to edit.");
      return;
    }
    setEditRows(
      activeRows.map((r) => ({
        month: r.month || "",
        month2: r.month2 || "",
        days_in_month: String(r.days_in_month ?? ""),
        week_off: String(r.week_off ?? ""),
        holidays: String(r.holidays ?? ""),
        working_days: String(r.working_days ?? ""),
      }))
    );
    setWorkingTouchedMonths(new Set());
    setAllEditOpen(true);
  }, [rows]);

  //Created By Cursor
  const closeAllEdit = useCallback(() => {
    if (saving) return;
    setAllEditOpen(false);
    setEditRows([]);
    setWorkingTouchedMonths(new Set());
  }, [saving]);

  //Created By Cursor
  const onEditRowChange = useCallback(
    (month, key, value, isWorkingField = false) => {
      setEditRows((prev) =>
        prev.map((row) => {
          if (row.month !== month) return row;
          const next = { ...row, [key]: value };
          if (isWorkingField) return next;
          if (workingTouchedMonths.has(month)) return next;
          if (
            key === "days_in_month" ||
            key === "week_off" ||
            key === "holidays"
          ) {
            const days = toInt(
              key === "days_in_month" ? value : next.days_in_month
            );
            const week = toInt(key === "week_off" ? value : next.week_off);
            const hol = toInt(key === "holidays" ? value : next.holidays);
            next.working_days = String(Math.max(0, days - week - hol));
          }
          return next;
        })
      );
      if (isWorkingField) {
        setWorkingTouchedMonths((prev) => {
          const next = new Set(prev);
          next.add(month);
          return next;
        });
      }
    },
    [workingTouchedMonths]
  );

  const toggleMonthStatus = useCallback(
    async (item) => {
      const month = item?.month;
      if (!month || !sessionId) return;

      const active = isActiveRow(item);
      const nextStatus = active ? "InActive" : "Active";
      const label = item.month2 ? `${item.month2} (${month})` : month;
      if (
        !window.confirm(
          `Mark ${label} as ${nextStatus}?`
        )
      ) {
        return;
      }

      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) {
        myToast.notify_warning("Missing school code.");
        return;
      }

      setRemovingMonth(month);
      try {
        const params = {
          school_code: schoolCode,
          session_id: sessionId,
          month,
          status: nextStatus,
        };
        logFullUrl("updateSchoolWorkingDaysStatus.php", params);
        const response = await axios
          .get(`${API_BASE()}updateSchoolWorkingDaysStatus.php`, { params })
          .then((res) => res.data);

        if (response?.api_status === "OK") {
          myToast.notify_success(`${label} marked ${nextStatus}.`);
          await loadRows({ soft: true });
        } else {
          myToast.notify_warning(
            response?.api_remark || "Failed to update status."
          );
        }
      } catch (error) {
        console.error("Error updating school working days status:", error);
        myToast.notify_warning("Failed to update status.");
      } finally {
        setRemovingMonth("");
      }
    },
    [loadRows, sessionId]
  );

  //Created By Cursor
  const saveAllEdit = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode || !sessionId) {
      myToast.notify_warning("Missing school or session.");
      return;
    }

    if (!editRows.length) {
      myToast.notify_warning("No months to save.");
      return;
    }

    for (const row of editRows) {
      const label = row.month2 || row.month || "Month";
      const daysRaw = String(row.days_in_month ?? "").trim();
      const weekRaw = String(row.week_off ?? "").trim();
      const holRaw = String(row.holidays ?? "").trim();
      const workRaw = String(row.working_days ?? "").trim();

      if (daysRaw === "" || weekRaw === "" || holRaw === "" || workRaw === "") {
        myToast.notify_warning(
          `${label}: days, week off, holidays and working days are all required.`
        );
        return;
      }

      const days_in_month = toInt(daysRaw);
      const week_off = toInt(weekRaw);
      const holidays = toInt(holRaw);
      const working_days = toInt(workRaw);

      if (days_in_month < 1 || days_in_month > 31) {
        myToast.notify_warning(
          `${label}: days in month must be between 1 and 31.`
        );
        return;
      }
      if (week_off < 0 || holidays < 0 || working_days < 0) {
        myToast.notify_warning(`${label}: values cannot be negative.`);
        return;
      }
    }

    const monthsPayload = editRows.map((row) => ({
      month: row.month,
      days_in_month: toInt(row.days_in_month),
      week_off: toInt(row.week_off),
      holidays: toInt(row.holidays),
      working_days: toInt(row.working_days),
    }));

    setSaving(true);
    try {
      const params = {
        school_code: schoolCode,
        session_id: sessionId,
        months: JSON.stringify(monthsPayload),
      };
      logFullUrl("updateSchoolWorkingDays.php", params);
      const response = await axios
        .get(`${API_BASE()}updateSchoolWorkingDays.php`, { params })
        .then((res) => res.data);

      if (response?.api_status === "OK") {
        myToast.notify_success("Saved working days.");
        setAllEditOpen(false);
        setEditRows([]);
        setWorkingTouchedMonths(new Set());
        await loadRows({ soft: true });
      } else {
        myToast.notify_warning(
          response?.api_remark || "Failed to update working days."
        );
      }
    } catch (error) {
      console.error("Error updating school working days:", error);
      myToast.notify_warning("Failed to update working days.");
    } finally {
      setSaving(false);
    }
  }, [editRows, loadRows, sessionId]);

  const recalculate = useCallback(async () => {
    if (!sessionId) {
      myToast.notify_warning("Select a session first.");
      return;
    }
    if (hasManualEntry) {
      myToast.notify_warning(
        "Recalculate is disabled because manual entries exist."
      );
      return;
    }
    if (
      !window.confirm(
        "Recalculate working days from holidays and week offs for this session? Existing auto rows will be refreshed."
      )
    ) {
      return;
    }

    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      myToast.notify_warning("Missing school code.");
      return;
    }

    setRefreshing(true);
    try {
      const params = { school_code: schoolCode, session_id: sessionId };
      logFullUrl("RefreshSchoolWorkingDays.php", params);
      const response = await axios
        .get(`${API_BASE()}RefreshSchoolWorkingDays.php`, { params })
        .then((res) => res.data);

      if (response?.api_status === "OK") {
        myToast.notify_success("Recalculated successfully.");
        const data = Array.isArray(response.data) ? response.data : [];
        setRows(
          data.filter(
            (r) => !r.session_id || String(r.session_id) === String(sessionId)
          )
        );
      } else {
        myToast.notify_warning(
          response?.api_remark || "Failed to recalculate."
        );
      }
    } catch (error) {
      console.error("Error refreshing school working days:", error);
      myToast.notify_warning("Failed to recalculate.");
    } finally {
      setRefreshing(false);
    }
  }, [hasManualEntry, sessionId]);

  const busy = loading || refreshing || saving || !!removingMonth;

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page swd-page"
      style={{ fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> School Working Days</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell swd-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaBusinessTime />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">School Working Days</h1>
              <p className="sm-hero__sub">
                {sessionName
                  ? `${sessionName} · ${stats.months} active month${
                      stats.months === 1 ? "" : "s"
                    }`
                  : "Select a session to load working days"}
                {manualCount > 0 ? ` · ${manualCount} manual` : ""}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className="swd-session"
              onClick={() => setSessionDialogOpen(true)}
              disabled={busy}
              title="Change session"
            >
              <span className="swd-session__icon" aria-hidden>
                <FaCalendarAlt />
              </span>
              <span className="swd-session__text">
                <small>Session</small>
                <strong>{sessionName || "Select session"}</strong>
              </span>
              <span className="swd-session__hint">Change</span>
            </button>
            <button
              type="button"
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              onClick={() => loadRows({ soft: true })}
              disabled={busy || !sessionId}
              aria-label="Refresh"
              title="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
            <button
              type="button"
              className="swd-cta"
              onClick={recalculate}
              disabled={busy || hasManualEntry || !sessionId}
              title={
                hasManualEntry
                  ? "Disabled — manual entries exist"
                  : "Recalculate from holidays & week offs"
              }
            >
              <FaSyncAlt aria-hidden />
              <span>Recalculate</span>
            </button>
          </div>
        </header>

        <div className="swd-body">
          {hasManualEntry ? (
            <div className="swd-banner" role="status">
              <strong>Manual entries</strong>
              <span>
                Recalculate is disabled so those values are not overwritten.
              </span>
            </div>
          ) : null}

          {!sessionReady || (sessionId && loading) ? (
            <div className="swd-loading">
              <MyLoader />
            </div>
          ) : !sessionId ? (
            <div className="swd-empty">
              <div className="swd-empty__icon" aria-hidden>
                <FaCalendarAlt />
              </div>
              <p className="swd-empty__title">Select a session</p>
              <p className="swd-empty__hint">
                Working days are loaded session-wise. Choose a session to
                continue.
              </p>
              <button
                type="button"
                className="swd-btn swd-btn--primary"
                onClick={() => setSessionDialogOpen(true)}
              >
                Select session
              </button>
            </div>
          ) : rows.length === 0 ? (
            <div className="swd-empty">
              <div className="swd-empty__icon" aria-hidden>
                <FaBusinessTime />
              </div>
              <p className="swd-empty__title">No working days yet</p>
              <p className="swd-empty__hint">
                Use Recalculate to generate months for{" "}
                {sessionName || "this session"}.
              </p>
              <button
                type="button"
                className="swd-btn swd-btn--primary"
                onClick={recalculate}
                disabled={busy}
              >
                Recalculate
              </button>
            </div>
          ) : (
            <>
              <div className="swd-toolbar">
                <div className="swd-toolbar__copy">
                  <h2 className="swd-toolbar__title">Session overview</h2>
                  <p className="swd-toolbar__hint">
                    Totals below count active months only.
                  </p>
                </div>
                <button
                  type="button"
                  className="swd-btn swd-btn--primary"
                  onClick={openAllEdit}
                  disabled={busy || !rows.some((r) => isActiveRow(r))}
                  title="Edit all active months"
                >
                  <FaEdit aria-hidden /> Edit months
                </button>
              </div>

              <div className="swd-stats" aria-label="Session totals">
                <div className="swd-stat">
                  <span>Months</span>
                  <strong>{stats.months}</strong>
                </div>
                <div className="swd-stat">
                  <span>Total days</span>
                  <strong>{stats.days}</strong>
                </div>
                <div className="swd-stat">
                  <span>Week offs</span>
                  <strong>{stats.weekOff}</strong>
                </div>
                <div className="swd-stat">
                  <span>Holidays</span>
                  <strong>{stats.holidays}</strong>
                </div>
                <div className="swd-stat swd-stat--accent">
                  <span>Working days</span>
                  <strong>{stats.working}</strong>
                </div>
              </div>

              <div className="swd-table-wrap">
                <table className="swd-table">
                  <thead>
                    <tr>
                      <th>Sr.</th>
                      <th>Year-Month</th>
                      <th>Month</th>
                      <th>Days</th>
                      <th>Week Off</th>
                      <th>Holidays</th>
                      <th>Working</th>
                      <th>Entry Mode</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((item, index) => (
                      <WorkingDayRow
                        key={`${item.session_id || sessionId}-${item.month}-${index}`}
                        item={item}
                        index={index}
                        busy={busy}
                        onToggleStatus={toggleMonthStatus}
                      />
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3}>Active totals</td>
                      <td>{stats.days}</td>
                      <td>{stats.weekOff}</td>
                      <td>{stats.holidays}</td>
                      <td>
                        <span className="swd-working-pill">{stats.working}</span>
                      </td>
                      <td />
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      <MdlEditAllMonths
        open={allEditOpen}
        saving={saving}
        formRows={editRows}
        liveStats={liveEditStats}
        sessionName={sessionName}
        onChange={onEditRowChange}
        onClose={closeAllEdit}
        onSave={saveAllEdit}
      />

      {sessionDialogOpen ? (
        <MdlSelectSession
          action={() => setSessionDialogOpen(false)}
          action2={onSessionPicked}
        />
      ) : null}
    </div>
  );
}
